import ClassNames from "classnames";
import deepExtend from "deep-extend";
import { FunctionComponent } from "react";
import { APISite } from "../../../server/types/index.js";
import { getPictureById } from "../../selectors/pictures.js";
import { getActiveSite } from "../../selectors/sites.js";
import {
  defaultImageCropParams,
  defaultImportantImagePoint,
  defaultSettings,
} from "../../slices/modules/constants.js";
import { useAppSelector } from "../../store/hooks.js";
import { createAppSelector } from "../../store/utils.js";
import highlightsLayout4Styles from "../../stylesheets/components/highlights-layout-4.module.css";
import highlightsLayout6Styles from "../../stylesheets/components/highlights-layout-6.module.css";
import highlightsLayout7Styles from "../../stylesheets/components/highlights-layout-7.module.css";
import highlightsLayout8Styles from "../../stylesheets/components/highlights-layout-8.module.css";
import highlightsLayout9Styles from "../../stylesheets/components/highlights-layout-9.module.css";
import {
  BaseModuleProps,
  ColorScheme,
  HighlightModuleSettings,
  HighlightsItem,
  HighlightsLayout,
  HighlightsModuleSettings,
  ImageModuleSettings,
  Language,
  Modules,
  PageState,
  Pictures,
  TranslatedModule,
} from "../../types/index.js";
import {
  getActiveColorScheme,
  getModulesByParentId,
  getTranslatedButtonModules,
  getTranslatedModulesByParentId,
  resolveButtonLink,
} from "../../utils/utils.js";
import HighlightsLayout4 from "../HighlightsLayout4.js";
import HighlightsLayout5 from "../HighlightsLayout5.js";
import HighlightsLayout6 from "../HighlightsLayout6.js";
import HighlightsLayout7 from "../HighlightsLayout7.js";
import HighlightsLayout8 from "../HighlightsLayout8.js";
import HighlightsLayout9 from "../HighlightsLayout9.js";
import { getDynamicSettings } from "../ModuleSettings/HighlightsModuleSettings.js";
import ModuleWithHeadings from "../ModuleWithHeadings.js";
import MultiBox from "../MultiBox.js";
import RichEditorWrapper from "../RichEditorWrapper.js";
import ButtonModule from "./ButtonModule.js";

type Props = BaseModuleProps<HighlightsModuleSettings>;

const HighlightsModule: FunctionComponent<Props> = ({
  translatedModule: {
    settings: {
      textAlign,
      maxColumnsCount,
      collapsedLinesCount,
      displayType,
      mediaAspectRatio,
      layout,
      transparentSliderArrowBackground,
      mediaAlign,
    },
    translation: {
      settings: { title, subtitle },
      languageId,
    },
    id,
  },
  translatedModule,
  pageId,
  queries,
  isPreview,
  isFirstOnPage,
  activeModuleId,
}) => {
  const site = useAppSelector((state) => getActiveSite(state.sites));
  const scheme = useAppSelector((state) =>
    getActiveColorScheme(state.colorSchemes, site, translatedModule),
  );
  const { items, hasHighlights } = useAppSelector((state) =>
    selectItems(state, translatedModule, pageId, isPreview),
  );
  const richEditor = useAppSelector((state) => state.richEditor);

  const layoutsForMultiBox: HighlightsLayout[] = [
    "layout-1",
    "layout-2",
    "layout-3",
  ];
  const isLayoutForMultiBox = layoutsForMultiBox.includes(layout);

  return (
    <ModuleWithHeadings
      id={id}
      className="HighlightsModule"
      title={title}
      subtitle={subtitle}
      colors={{ color: scheme.main.text }}
    >
      {layout === "layout-4" && (
        <HighlightsLayout4
          {...{
            isFirstOnPage,
            scheme,
            subtitle,
            textAlign,
            title,
            items,
            queries,
            displayType,
            isPreview,
            activeModuleId,
            transparentSliderArrowBackground,
            languageId,
          }}
        />
      )}

      {layout === "layout-5" && (
        <HighlightsLayout5
          {...{
            isFirstOnPage,
            scheme,
            subtitle,
            textAlign,
            title,
            items,
            queries,
            isPreview,
          }}
        />
      )}

      {layout === "layout-6" && (
        <HighlightsLayout6
          {...{
            isFirstOnPage,
            scheme,
            subtitle,
            textAlign,
            title,
            items,
            collapsedLinesCount,
            languageId,
            richEditor,
            isPreview,
            displayType,
            activeModuleId,
            transparentSliderArrowBackground,
            mediaAlign,
            maxColumnsCount,
            queries,
          }}
        />
      )}

      {layout === "layout-7" && (
        <HighlightsLayout7
          {...{
            isFirstOnPage,
            scheme,
            subtitle,
            textAlign,
            title,
            items,
            queries,
            collapsedLinesCount,
            languageId,
            richEditor,
            isPreview,
            displayType,
            mediaAspectRatio,
            activeModuleId,
            transparentSliderArrowBackground,
            mediaAlign,
          }}
        />
      )}

      {layout === "layout-8" && (
        <HighlightsLayout8
          {...{
            isFirstOnPage,
            scheme,
            subtitle,
            textAlign,
            title,
            items,
            queries,
            collapsedLinesCount,
            languageId,
            richEditor,
            isPreview,
            activeModuleId,
            maxColumnsCount,
            displayType,
            transparentSliderArrowBackground,
          }}
        />
      )}

      {layout === "layout-9" && (
        <HighlightsLayout9
          {...{
            isFirstOnPage,
            scheme,
            subtitle,
            textAlign,
            title,
            items,
            queries,
            isPreview,
            activeModuleId,
            transparentSliderArrowBackground,
            languageId,
          }}
        />
      )}

      {isLayoutForMultiBox && (
        <MultiBox
          {...{
            title,
            subtitle,
            textAlign,
            mediaAspectRatio,
            isPreview,
            queries,
            scheme,
            layout,
            languageId,
            displayType,
            items,
            isFirstOnPage,
            maxColumnsCount,
            placeholdersShown: !hasHighlights,
            collapsedLinesCount: collapsedLinesCount,
            richEditor,
            activeModuleId,
          }}
        />
      )}
    </ModuleWithHeadings>
  );
};

const getDefaultItemsCount = (layout: HighlightsLayout): number => {
  switch (layout) {
    case "layout-4":
      return 3;
    case "layout-5":
      return 4;
    case "layout-9":
      return 3;
    default:
      return 2;
  }
};

const getHighlightButtonClass = (layout: HighlightsLayout): string => {
  switch (layout) {
    case "layout-4":
      return highlightsLayout4Styles.HighlightsLayout4__Button;
    case "layout-6":
      return highlightsLayout6Styles.HighlightsLayout6__Button;
    case "layout-7":
      return highlightsLayout7Styles.HighlightsLayout7__Button;
    case "layout-8":
      return highlightsLayout8Styles.HighlightsLayout8__Button;
    case "layout-9":
      return highlightsLayout9Styles.HighlightsLayout9__Button;
    default:
      return "";
  }
};

const getItems = ({
  pictures,
  modules,
  module,
  pageId,
  languageId,
  isPreview,
  scheme,
  pages,
  site,
}: {
  pictures: Pictures;
  modules: Modules;
  module: TranslatedModule<HighlightsModuleSettings>;
  pageId: string;
  languageId: Language;
  isPreview: boolean;
  scheme: ColorScheme;
  pages: PageState;
  site: APISite;
}): { items: HighlightsItem[]; hasHighlights: boolean } => {
  const {
    siteId,
    settings: { layout },
  } = module;
  const highlights = getTranslatedModulesByParentId<HighlightModuleSettings>({
    modules,
    parentId: module.id,
    pageId,
    moduleType: "HighlightModule",
    languageId,
  });

  const hasHighlights = !!highlights.length;

  const finalHighlights = hasHighlights
    ? highlights
    : // Construct Array with sequential numbers as strings
      Object.keys(Array.from(new Array(getDefaultItemsCount(layout)))).map<
        TranslatedModule<HighlightModuleSettings>
      >((id) => {
        const highlightModule: TranslatedModule<HighlightModuleSettings> = {
          id,
          colorSchemeId: null,
          siteId,
          type: "HighlightModule",
          pageId,
          parentId: module.id,
          settings: deepExtend({}, defaultSettings.HighlightModule.global),
          translation: {
            languageId,
            exists: true,
            settings: deepExtend({}, defaultSettings.HighlightModule.language),
          },
          languages: [languageId],
        };
        return highlightModule;
      });

  // TODO: check if pictures are loaded
  // Use getPictureById to retrieve pictures and important point informations!
  const items: HighlightsItem[] = finalHighlights.map((highlight) => {
    const {
      id: moduleId,
      settings: { textAlign },
      translation: {
        settings: { title, subtitle },
      },
    } = highlight;

    const images = getModulesByParentId<ImageModuleSettings>(
      modules,
      moduleId,
      pageId,
      "ImageModule",
    );

    const imageSettings = images[0]?.settings;

    const picture = getPictureById(pictures, imageSettings?.pictureId);

    const translatedButtons = getTranslatedButtonModules({
      languageId,
      modules,
      moduleId,
      pageId,
    });

    const buttons = translatedButtons.map((translatedButton) => (
      <ButtonModule
        key={translatedButton.id}
        translatedModule={translatedButton}
        scheme={scheme}
        isPreview={isPreview}
        className={getHighlightButtonClass(layout)}
      />
    ));

    const firstButton = translatedButtons[0];
    const link =
      firstButton &&
      resolveButtonLink({
        isPreview,
        pages,
        site,
        translatedModule: firstButton,
      });

    const dynamicSettings = getDynamicSettings(layout);

    const item: HighlightsItem = {
      id: moduleId,
      title,
      subtitle,
      textAlign,
      picture,
      link,
      text: (
        <RichEditorWrapper
          module={highlight}
          pageId={pageId}
          languageId={languageId}
          isPreview={isPreview}
          scheme={scheme}
          className={ClassNames({
            [`TextAlign--${textAlign.description}`]:
              dynamicSettings.highlightTextAlign,
          })}
        />
      ),
      buttons,
      imageDetail: {
        crop: imageSettings?.crop ?? defaultImageCropParams,
        importantPoint:
          imageSettings?.importantPoint ?? defaultImportantImagePoint,
      },
    };

    return item;
  });

  return { items, hasHighlights };
};

const selectItems = createAppSelector(
  [
    (state) => state.mediaLibrary.pictures,
    (state) => state.modules,
    (state) => state.pages,
    (state) => state.sites,
    (state) => state.colorSchemes,
    (_, module: TranslatedModule<HighlightsModuleSettings>) => module,
    (_, _module: TranslatedModule<HighlightsModuleSettings>, pageId: string) =>
      pageId,
    (
      _,
      _module: TranslatedModule<HighlightsModuleSettings>,
      _pageId: string,
      isPreview: boolean,
    ) => isPreview,
  ],
  (
    pictures,
    modules,
    pages,
    sites,
    colorSchemes,
    module,
    pageId,
    isPreview,
  ) => {
    const site = getActiveSite(sites);
    const scheme = getActiveColorScheme(colorSchemes, site, module);
    return getItems({
      pictures,
      site,
      pages,
      modules,
      module,
      pageId,
      languageId: module.translation.languageId,
      isPreview,
      scheme,
    });
  },
);

export default HighlightsModule;
